import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

class CubeyBundleTiers extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;
  #processing = false;

  connectedCallback() {
    if (this.#abortController) return;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.querySelectorAll('.cubey-tier-option__radio').forEach((radio) => {
      radio.addEventListener('change', this.#updateAllPrices, { signal });
    });

    this.querySelectorAll('[data-bundle-select]').forEach((select) => this.#initCustomSelect(select, signal));

    document.addEventListener('click', this.#handleOutsideClick, { signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal });

    const addButton = this.querySelector('[data-bundle-add-button]');
    addButton?.addEventListener('click', this.#handleAddClick, { signal });

    this.#updateAllPrices();

    // Signal to CSS that this block initialized successfully, so the native
    // variant picker / buy buttons can be safely hidden. If this line never
    // runs (script failed to load, etc.), the native buttons stay visible.
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /**
   * Wires up one custom dropdown (button + listbox + hidden input), replacing
   * the native <select> so the open option list can be styled to match the
   * site instead of using the browser's default OS listbox.
   * @param {Element} select
   * @param {AbortSignal} signal
   */
  #initCustomSelect(select, signal) {
    const trigger = select.querySelector('[data-select-trigger]');
    const list = select.querySelector('[data-select-list]');
    const input = select.querySelector('[data-bundle-item-select]');
    const triggerText = select.querySelector('[data-select-trigger-text]');
    if (!trigger || !list || !input) return;

    trigger.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        this.#toggleSelect(select);
      },
      { signal }
    );

    list.querySelectorAll('[data-select-option]').forEach((option) => {
      option.addEventListener(
        'click',
        () => {
          if (option.getAttribute('aria-disabled') === 'true') return;
          this.#chooseOption(select, option);
        },
        { signal }
      );
    });
  }

  /** @param {Element} select */
  #toggleSelect(select) {
    const isOpen = select.hasAttribute('data-open');
    this.#closeAllSelects();
    if (!isOpen) this.#openSelect(select);
  }

  /** @param {Element} select */
  #openSelect(select) {
    const trigger = select.querySelector('[data-select-trigger]');
    const list = select.querySelector('[data-select-list]');
    select.setAttribute('data-open', '');
    trigger?.setAttribute('aria-expanded', 'true');
    list?.removeAttribute('hidden');
  }

  #closeAllSelects() {
    this.querySelectorAll('[data-bundle-select][data-open]').forEach((select) => {
      const trigger = select.querySelector('[data-select-trigger]');
      const list = select.querySelector('[data-select-list]');
      select.removeAttribute('data-open');
      trigger?.setAttribute('aria-expanded', 'false');
      list?.setAttribute('hidden', '');
    });
  }

  #handleOutsideClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target) && event.target.closest('[data-bundle-select]')) return;
    this.#closeAllSelects();
  };

  #handleKeydown = (event) => {
    if (event.key === 'Escape') this.#closeAllSelects();
  };

  /**
   * @param {Element} select
   * @param {Element} option
   */
  #chooseOption(select, option) {
    const input = select.querySelector('[data-bundle-item-select]');
    const triggerText = select.querySelector('[data-select-trigger-text]');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = option.getAttribute('data-value') || '';
    input.dataset.price = option.getAttribute('data-price') || '0';
    input.dataset.compareAtPrice = option.getAttribute('data-compare-at-price') || '0';
    if (triggerText) triggerText.textContent = option.textContent?.trim() || '';

    select.querySelectorAll('[data-select-option]').forEach((el) => {
      el.removeAttribute('data-selected');
      el.setAttribute('aria-selected', 'false');
    });
    option.setAttribute('data-selected', '');
    option.setAttribute('aria-selected', 'true');

    this.#closeAllSelects();
    this.#updateAllPrices();
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  #updateAllPrices = () => {
    this.querySelectorAll('.cubey-tier-option').forEach((tier) => this.#updateTierPrice(tier));
    this.#updateButtonPrice();
  };

  #updateButtonPrice() {
    const priceEl = this.querySelector('[data-bundle-add-price]');
    const tier = this.#getSelectedTier();
    if (!priceEl) return;
    priceEl.textContent = tier?.dataset.currentPrice ? this.#money(Number(tier.dataset.currentPrice)) : '';
  }

  /** @param {Element} tier */
  #updateTierPrice(tier) {
    let price = 0;
    let comparePrice = 0;

    tier.querySelectorAll('[data-bundle-item-select], [data-bundle-item-fixed-id]').forEach((input) => {
      price += Number(input.dataset.price || 0);
      comparePrice += Number(input.dataset.compareAtPrice || input.dataset.price || 0);
    });

    const fixedPrice = Number(tier.dataset.fixedPrice || 0);
    let finalPrice;
    let displayDiscountPercent;

    if (fixedPrice > 0) {
      finalPrice = fixedPrice;
      displayDiscountPercent = price > 0 ? Math.round((1 - fixedPrice / price) * 100) : 0;
    } else {
      const discountPercent = Number(tier.dataset.discountPercent || 0);
      finalPrice = discountPercent > 0 ? Math.round(price * (1 - discountPercent / 100)) : price;
      displayDiscountPercent = discountPercent;
    }

    tier.dataset.currentPrice = String(finalPrice);

    const priceEl = tier.querySelector('[data-bundle-tier-price]');
    const comparePriceEl = tier.querySelector('[data-bundle-tier-compare-price]');
    const discountEl = tier.querySelector('[data-bundle-tier-discount]');
    if (priceEl) priceEl.textContent = this.#money(finalPrice);
    if (comparePriceEl) {
      comparePriceEl.textContent = comparePrice > finalPrice ? this.#money(comparePrice) : '';
    }
    if (discountEl) {
      discountEl.textContent = displayDiscountPercent > 0 ? `Save ${displayDiscountPercent}%` : '';
    }
  }

  #getSelectedTier() {
    const radio = this.querySelector('.cubey-tier-option__radio:checked');
    return radio ? radio.closest('.cubey-tier-option') : null;
  }

  /** @param {Element} tier */
  #getTierItems(tier) {
    /** @type {{ id: number, quantity: number }[]} */
    const items = [];

    tier.querySelectorAll('[data-bundle-item-select], [data-bundle-item-fixed-id]').forEach((input) => {
      const value = Number(input.value);
      if (value) items.push({ id: value, quantity: 1 });
    });

    return items;
  }

  #handleAddClick = async () => {
    if (this.#processing) return;

    const tier = this.#getSelectedTier();
    const status = this.querySelector('[data-bundle-status]');
    const button = this.querySelector('[data-bundle-add-button]');

    if (!tier) return;

    const items = this.#getTierItems(tier);
    if (items.length === 0) return;

    this.#processing = true;
    this.toggleAttribute('data-processing', true);
    button?.setAttribute('aria-busy', 'true');
    if (status) {
      status.textContent = '';
      delete status.dataset.state;
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items }) }));
      const result = await response.json();

      if (!response.ok || result.errors || result.status) {
        const message = typeof result.errors === 'string' ? result.errors : result.message || 'Unable to add this bundle. Please try again.';
        throw new Error(message);
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'cubey-bundle-tiers', {
          source: 'cubey-bundle-tiers',
          productId: this.dataset.productId,
          itemCount: cart.item_count,
        })
      );

      if (status) {
        status.textContent = 'Added to your cart.';
        status.dataset.state = 'success';
      }
    } catch (error) {
      const message = this.dataset.errorMessage || (error instanceof Error ? error.message : 'Unable to add this bundle. Please try again.');
      this.dispatchEvent(new CartErrorEvent(this.id || 'cubey-bundle-tiers', message, '', ''));
      if (status) {
        status.textContent = message;
        status.dataset.state = 'error';
      }
    } finally {
      this.#processing = false;
      this.toggleAttribute('data-processing', false);
      button?.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('cubey-bundle-tiers')) {
  customElements.define('cubey-bundle-tiers', CubeyBundleTiers);
}
